import React, { useEffect, useState } from "react";
import "tailwindcss/tailwind.css";
import time from "../../Assets/image2.png";
import { useDispatch, useSelector } from "react-redux";
import {
  requestBackgroundImage,
  requestLoginAction,
  setResetStateLogin,
} from "../../Redux/LoginState/LoginActionCreator";
import LoginForm from "./LoginForm";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Loader from "../../components/Loader";
import LOCAL_KEYS, { setToLocalStorage } from "../../helpers/Localstorage";
import Swal from "sweetalert2";
import { requestGetFileAction } from "../../Redux/FileState/uploadFileActionCreator";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import { typeOfApplication } from "../../Constants";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [dynamicImages, setDynamicImages] = useState([time]);
  const { loginResponse, isLoading, loginError } = useSelector(
    (state) => state.LoginReducer
  );
  const BackgroundImageReducer = useSelector((state) => state.FileReducer);

  // Accept impersonation session from admin portal (token + user in query params)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isImpersonate = params.get("impersonate") === "true";
    const token = params.get("token");
    const refreshToken = params.get("refreshToken");
    const encodedUser = params.get("user");

    if (!isImpersonate || !token || !encodedUser) {
      return;
    }

    try {
      const userDetails = JSON.parse(encodedUser);
      const userType = userDetails?.acctType?.toUpperCase();

      if (userType !== "USER") {
        toast.error("Invalid impersonation user type");
        return;
      }

      // Clear previous session so we never keep the last impersonated user
      localStorage.clear();

      setToLocalStorage(LOCAL_KEYS.ACCESS_TOKEN, token);
      if (refreshToken) {
        setToLocalStorage(LOCAL_KEYS.REFRESH_TOKEN, refreshToken);
      }
      setToLocalStorage(LOCAL_KEYS.USER_DETAILS, userDetails);
      setToLocalStorage("isImpersonating", true);

      window.history.replaceState({}, document.title, "/");
      toast.success(
        `Viewing as ${userDetails.firstName || ""} ${userDetails.lastName || ""}`.trim()
      );
      // Full reload so all components pick up the new userDetails from localStorage
      window.location.replace("/");
    } catch (error) {
      toast.error("Failed to start impersonation session");
    }
  }, [navigate]);

  useEffect(() => {
    if (loginResponse !== null) {
      // If we get access denied message, show error and reset
      if (loginResponse.message === "Access denied") {
        Swal.fire({
          title: "Error",
          text: "Access denied. Please check your credentials.",
          icon: "error",
          confirmButtonText: "Ok",
        });
        dispatch(setResetStateLogin());
        return;
      }
 
      // Check if userDetails exists and has acctType
      if (loginResponse.userDetails && loginResponse.userDetails.acctType) {
        const userType = loginResponse.userDetails.acctType.toUpperCase();
        if (userType === "USER" || userType === "User".toUpperCase()) {
        setToLocalStorage(LOCAL_KEYS.ACCESS_TOKEN, loginResponse.access_token);
          setToLocalStorage(LOCAL_KEYS.REFRESH_TOKEN, loginResponse.refresh_token);
        setToLocalStorage(LOCAL_KEYS.USER_DETAILS, loginResponse.userDetails);
          
        if (loginResponse.message) {
          setToLocalStorage(LOCAL_KEYS.EXPIRY, true);
          toast.error("Your password is expired please reset your password");
          dispatch(setResetStateLogin());
          }
        } else {
          Swal.fire({
            title: "Error",
            text: "Invalid User Type",
            icon: "error",
            confirmButtonText: "Ok",
          });
          dispatch(setResetStateLogin());
        }
      } else {
        // Handle case where userDetails is null
        Swal.fire({
          title: "Error",
          text: "Invalid user details",
          icon: "error",
          confirmButtonText: "Ok",
        });
        dispatch(setResetStateLogin());
      }
    }
  }, [loginResponse]);

  useEffect(() => {
    if (loginError && !isLoading) {
      toast.error("Username or password is invalid! Please try again");
      dispatch(setResetStateLogin());
    }
  }, [loginError]);

  // useEffect(() => {
  //   dispatch(requestBackgroundImage());
  // }, []);

  useEffect(() => {
    if (
      BackgroundImageReducer?.response &&
      BackgroundImageReducer?.response?.blobUrl &&
      !BackgroundImageReducer?.loading
    ) {
      setDynamicImages([BackgroundImageReducer?.response?.blobUrl, time]);
    }
  }, [BackgroundImageReducer]);

  const settings = {
    dots: false,
    fade: true,
    infinite: true,
    speed: 2000,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    waitForAnimate: false,
  };

  return (
    <div className="flex min-h-screen h-screen w-screen overflow-hidden bg-gray-50">
      {/* Background image section - visible on sm and md screens */}
      <div className="lg:hidden absolute inset-0 w-full h-full">
        <img
          className="w-full h-full object-cover"
          src={time}
          alt="Background"
        />
        <div className="absolute inset-0 bg-white/60 backdrop-blur-sm" />
      </div>

      {/* Right side with background image - only visible on lg screens */}
      <div className="hidden lg:flex lg:w-[70.83%] h-screen relative overflow-hidden">
        <div className="w-full h-full relative">
          <img
            className="absolute inset-0 w-full h-full object-cover"
            src={time}
            alt="Background"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent z-10" />
          <div className="absolute bottom-0 left-0 right-0 p-12 z-20">
            <h2 className="text-4xl font-medium text-gray-400 mb-4 drop-shadow-lg tracking-wide">
              Transforming Ideas into Digital Excellence
            </h2>
          </div>
        </div>
      </div>

      {/* Login form container */}
      <div className="w-full lg:w-[29.17%] flex items-center justify-center h-screen relative z-10">
        <div className="w-full max-w-md px-4 sm:px-8 py-6 flex flex-col items-center justify-center">
          <div className="w-full flex flex-col items-center justify-center">
            <div className="w-full lg:bg-transparent backdrop-blur-md bg-white/90 lg:bg-white/10 rounded-2xl p-6 sm:p-8 shadow-2xl border border-gray-200 lg:border-white/20 lg:border-none lg:shadow-none lg:rounded-none">
              <LoginForm />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
